import { TranslateService } from '@ngx-translate/core';
import { InternetAndPerformaceDeviceErrorType } from '../services/system-status-panel.service';
import { DeviceErrorType } from './device-manger';

export enum SystemItemType {
  MICROPHONE = 'Microphone',
  CAMERA = 'Camera',
  INTERNET_CONNECTION = 'Internet connection',
  DEVICE_PERFORMANCE = 'Device performance',
}

export enum SystemItemState {
  GOOD = 'Good',
  NEEDS_ATTENTION = 'Needs attention',
  INACTIVE = 'Inactive',
}

export interface SystemItemTips {
  currentUserIssue: { [key: string]: string[] };
  otherUserIssue?: {
    stepsForSelf: string[];
    stepsForOther: string[];
  };
}

export class SystemStatus {
  private cameraTips: SystemItemTips;
  private microphoneTips: SystemItemTips;
  private internetConnectionTips: SystemItemTips;
  private devicePerformanceTips: SystemItemTips;

  noInputTipsForCamera = [
    this.translate.instant('Close any other apps that may be using the camera'),
    this.translate.instant('Refresh browser and rejoin call'),
    this.translate.instant('Update browser and restart device'),
  ];

  noInputTipsForMic = [
    this.translate.instant('Close any other apps that may be using the mic'),
    this.translate.instant('Refresh browser and rejoin call'),
    this.translate.instant('Update browser and restart device'),
  ];

  noDeviceTips = [
    this.translate.instant(
      'Reconnect devices and check system settings to verify they are working',
    ),
    this.translate.instant('Restart browser or device'),
    this.translate.instant('If issues persist, try a different device or browser'),
  ];

  constructor(private translate: TranslateService) {
    this.cameraTips = {
      currentUserIssue: {
        [DeviceErrorType.NO_INPUT_DETECTED]: this.noInputTipsForCamera,
        [DeviceErrorType.GET_USER_MEDIA_TIMEOUT]: this.noInputTipsForCamera,
        [DeviceErrorType.NOT_FOUND]: this.noDeviceTips,
        [DeviceErrorType.DEFAULT_ERROR]: this.noDeviceTips,
      },
    };

    this.microphoneTips = {
      currentUserIssue: {
        [DeviceErrorType.NO_INPUT_DETECTED]: this.noInputTipsForMic,
        [DeviceErrorType.GET_USER_MEDIA_TIMEOUT]: this.noInputTipsForMic,
        [DeviceErrorType.NOT_FOUND]: this.noDeviceTips,
        [DeviceErrorType.DEFAULT_ERROR]: this.noDeviceTips,
      },
    };

    this.internetConnectionTips = {
      currentUserIssue: {
        [InternetAndPerformaceDeviceErrorType.DEFAULT_ERROR]: [
          this.translate.instant('Switch off your camera and close screenshares'),
          this.translate.instant('Close any external apps running on your device'),
          this.translate.instant('Close any Web Viewer apps on your board'),
          this.translate.instant('Close any other open tabs on your browser'),
        ],
      },
      otherUserIssue: {
        stepsForSelf: [this.translate.instant('Close any Web Viewer apps on your board')],
        stepsForOther: [
          this.translate.instant('Switch off their camera and close screenshares'),
          this.translate.instant('Close any external apps running on their device'),
          this.translate.instant('Close any other open tabs on their browser'),
        ],
      },
    };

    this.devicePerformanceTips = {
      currentUserIssue: {
        [InternetAndPerformaceDeviceErrorType.DEFAULT_ERROR]: [
          this.translate.instant('Verify noise cancellation is turned off'),
          this.translate.instant('Verify virtual background is turned off'),
          this.translate.instant('Close any apps on your board'),
          this.translate.instant('Continue working on a new board'),
          this.translate.instant('Close any external apps running on your device'),
        ],
      },
      otherUserIssue: {
        stepsForSelf: [
          this.translate.instant('Close any apps on your board'),
          this.translate.instant('Continue working on a new board'),
        ],
        stepsForOther: [
          this.translate.instant('Verify noise cancellation is turned off'),
          this.translate.instant('Verify virtual background is turned off'),
          this.translate.instant('Close any external apps running on their device'),
        ],
      },
    };
  }

  public getUserSystemStatusTips() {
    return {
      [SystemItemType.MICROPHONE]: this.microphoneTips,
      [SystemItemType.CAMERA]: this.cameraTips,
      [SystemItemType.INTERNET_CONNECTION]: this.internetConnectionTips,
      [SystemItemType.DEVICE_PERFORMANCE]: this.devicePerformanceTips,
    };
  }

  public getSystemIcons() {
    return {
      [SystemItemType.MICROPHONE]: 'mic_icon',
      [SystemItemType.CAMERA]: 'videocam_icon',
      [SystemItemType.INTERNET_CONNECTION]: 'connection_states',
      [SystemItemType.DEVICE_PERFORMANCE]: 'device_performance',
    };
  }
}
